<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'

import TitleBackDate from './title-back-date.vue'
import CustomerControl from './customer-control.vue'
import SaleControl from './sale-control.vue'
import CurrencyInput from './currency-input.vue'
import { round } from 'lodash'

export default {
  name: 'paymentDetail',
  props: ['value', 'validate'],
  components: {
    CustomerControl,
    SaleControl,
    CurrencyInput
  },
  data() {
    return {
      errors: new Errors(),
      model: {}
    }
  },
  created() {
    this.model = this.value
  },
  computed: {
    ...mapState('saleInvoiceStore', [
      'customers',
      'currencies',
      'paymentMethods',
      'banks'
    ]),
    creditAmount() {
      let cus = this.customers.find(
        c => c.customer_id == this.model.customer_id
      )
      if (cus) {
        return cus.credit_amount
      }
      return 0
    },
    fxRateWithSign() {
      return this.currencies.map(el => ({
        currency_code: el.currency_code,
        is_default: el.is_default,
        fx_rate: el.is_default
          ? 1
          : el.last_exchange_rate
          ? el.last_exchange_rate.fx_rate
          : 0
      }))
    },
    defaultCurrencySign() {
      if (this.currencies) {
        const currency = this.currencies.find(c => c.is_default)
        return currency ? currency.currency_symbol : undefined
      }
      return undefined
    },
    defaultCurrencyId() {
      if (this.currencies) {
        const currency = this.currencies.find(c => c.is_default)
        return currency ? currency.currency_id : undefined
      }

      return undefined
    },
    paymentMethodList() {
      return this.paymentMethods.map(el => ({
        value: el.payment_method_id,
        label: el.payment_method
      }))
    },
    allowSelectBank() {
      return this.model.payment_method_id !== 1
    },
    banksList() {
      return this.banks.map(el => ({
        value: el.bank_id,
        label: el.bank_name,
        name: el.bank_name
      }))
    },
    accountByBankId() {
      const bank = this.banks.find(b => b.bank_id === this.model.bank_id)
      if (bank) {
        return bank.accounts.map(el => ({
          value: el.account_number,
          label: el.account_number,
          name: el.account_name
        }))
      }

      return []
    },
    defaultPrecision() {
      return this.$store.state.authUser.sys_setting.decimal_digit
    }
  },
  methods: {
    ...mapActions('saleInvoiceStore', ['onGetBank']),
    disabledDate(current) {
      let current_date = new Date() // Current date and time
      let allow_date = new Date(current_date) // Create a new Date object with the current date and time
      allow_date.setDate(
        current_date.getDate() - this.allow_back_date_sale_inovice - 1
      )
      return current && current.valueOf() < allow_date
    },
    invoiceDateChange() {
      if (this.model.current_date > this.model.stock_out_date) {
        this.$confirm({
          title: this.$t('are_you_sure_to_create_back_date'),
          content: h => h(TitleBackDate),
          okText: this.$t('yes'),
          okType: 'danger',
          cancelText: this.$t('no'),
          onOk: () => {
            this.model.product_detail = []
            this.model.quotation_id = ''
            this.model.contract_id = ''
            this.model.is_check_back_date = true
          },
          onCancel: () => {
            this.model.stock_out_date = this.model.current_date
          }
        })
      }
      if (!this.model.current_date > this.model.stock_out_date) {
        this.updateNextPaymentDate()
      }
      if (this.model.current_date == this.model.stock_out_date) {
        this.model.product_detail = []
        this.model.is_check_back_date = false
      }
    },
    updateNextPaymentDate() {
      if (this.model.stock_out_date && this.model.amount_due > 0) {
        this.model.next_payment_date = moment(
          this.model.stock_out_date,
          'DD-MM-YYYY'
        )
          .add(this.model.credit_day, 'days')
          .format('DD-MM-YYYY')
      }
    },
    creditDayChange() {
      if (this.model.amount_due > 0) {
        this.updateNextPaymentDate()
      }
    },
    updateCurrency(currencyId) {
      this.model.input_currency_id = currencyId

      if (currencyId === this.defaultCurrencyId) {
        this.model.fx_rate = 1
        this.model.fx_rate_ratio = 1
        this.calcualteAmount()
        return
      }

      // find exchange from currency to default
      const exchangeRate = this.currencies.find(
        c => c.currency_id === this.model.input_currency_id
      )

      if (exchangeRate.last_exchange_rate !== undefined) {
        const { fx_rate, fx_rate_ratio } = exchangeRate.last_exchange_rate
        // update fx rate and ratio
        this.model.fx_rate = fx_rate
        this.model.fx_rate_ratio = fx_rate_ratio
      }
      this.calcualteAmount()
    },
    calcualteAmount() {
      const defaultPrecision = this.$store.state.authUser.sys_setting
        .decimal_digit

      this.model.total_after_discount =
        this.model.sub_total -
        this.model.input_discount * this.model.fx_rate_ratio

      if (this.model.vat_rate > 0) {
        this.model.vat_amount =
          (this.model.total_after_discount * this.model.vat_rate) / 100
      }

      this.model.grand_total =
        this.model.total_after_discount + this.model.vat_amount

      this.model.amount_paid =
        this.model.input_amount_paid * this.model.fx_rate_ratio
      this.model.amount_due =
        this.roundingNumber(this.model.grand_total) -
        this.roundingNumber(this.model.amount_paid)

      // next payment date if amount due is 0
      if (this.model.amount_due <= 0) {
        this.model.next_payment_date = null
      }
      if (this.model.amount_due > 0) {
        this.model.next_payment_date = moment(
          this.model.stock_out_date,
          'DD-MM-YYYY'
        )
          .add(this.model.credit_day, 'days')
          .format('DD-MM-YYYY')
      }
    },
    roundingNumber(value) {
      const defaultPrecision = this.$store.state.authUser.sys_setting
        .decimal_digit

      return round(
        parseFloat(this.$formatNumber(value, defaultPrecision), 10),
        defaultPrecision
      )
    },
    onPaymentMethodChange() {
      this.model.bank_id = ''
      this.model.account_number = ''
      this.model.account_name = ''
      this.model.bank_name = ''

      if (this.model.payment_method_id === 1) return

      // fetch bank
      this.onGetBank()
    },
    setAccountName(value, opt) {
      this.model.account_name = opt.data.props.name
    },
    onBankChange(value, opt) {
      this.model.bank_name = opt.data.props.name
    }
  },
  watch: {
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>

<template>
  <div>
    <h3 class="ts-text-gray-500">
      <i class="fas fa-wallet"></i>
      {{ $t('saleInvoice.paymentDetail') }}
    </h3>
    <div class="row">
      <div class="col-md-6">
        <div
          class="ts-border-2 ts-border-gray-200 ts-border-dashed ts-p-4 ts-rounded ts-h-full ts-space-y-2"
        >
          <div class="row">
            <div class="col-md-6">
              <label class="required ts-mb-0">{{
                $t('saleInvoice.invoiceDate')
              }}</label>
              <a-date-picker
                :allowClear="false"
                v-model="model.stock_out_date"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                class="ts-w-full"
                :disabled-date="disabledDate"
                @change="invoiceDateChange"
              ></a-date-picker>
              <div class="validate" v-if="errors.has('stock_out_date')">
                {{ errors.first('stock_out_date') }}
              </div>
            </div>
            <div class="col-md-6">
              <label class="ts-mb-0">{{ $t('saleInvoice.invoiceRef#') }}</label>
              <input
                v-model="model.reference_no"
                type="text"
                class="form-control"
                placeholder="Ex: INVC-0000-001"
              />
              <div class="validate" v-if="errors.has('reference_no')">
                {{ errors.first('reference_no') }}
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <CustomerControl v-model="value" />
              <div class="validate" v-if="errors.has('customer_id')">
                {{ errors.first('customer_id') }}
              </div>
            </div>
            <div class="col-md-6">
              <SaleControl v-model="value" />
              <div class="validate" v-if="errors.has('seller_id')">
                {{ errors.first('seller_id') }}
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <label class="ts-mb-0">{{ $t('saleInvoice.credit') }}</label>
              <div class="ts-grid ts-grid-cols-2 ts-gap-x-2">
                <div>
                  <input
                    :value="
                      creditAmount ? $defaultCurrencyWithCode(creditAmount) : 0
                    "
                    type="text"
                    class="form-control"
                    readonly
                  />
                </div>
                <input
                  v-model.number="model.credit_day"
                  type="number"
                  class="form-control"
                  placeholder="Days"
                  @change="creditDayChange"
                />
              </div>
            </div>
            <div class="col-md-6">
              <label class="ts-mb-0">{{
                $t('saleInvoice.exchangeRate')
              }}</label>

              <div
                class="ts-border ts-h-8 ts-rounded ts-p-1 ts-flex ts-items-center ts-overflow-hidden ts-space-x-2"
              >
                <span
                  v-for="(fx, index) in fxRateWithSign"
                  :key="index"
                  :class="{
                    'ts-font-semibold ts-text-blue-500': fx.is_default
                  }"
                  >{{ fx.currency_code + ' : ' + fx.fx_rate }}</span
                >
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <label class="ts-mb-0">{{ $t('saleInvoice.remark') }}</label>
              <textarea rows="5" class="form-control"></textarea>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div
          class="ts-border-2 ts-border-gray-200 ts-border-dashed ts-p-4 ts-rounded ts-h-full ts-space-y-2"
        >
          <div class="row">
            <div class="col-md-6">
              <label class="ts-mb-0">{{ $t('saleInvoice.subTotal') }}</label>
              <CurrencyInput
                :currencyId="defaultCurrencyId"
                :value="model.sub_total"
                :isDefault="true"
                class="ts-border ts-rounded ts-py-2 ts-bg-gray-100 ts-text-red-500 ts-font-semibold ts-font-robot"
              />
            </div>
            <div class="col-md-6">
              <label class="ts-mb-0">{{
                $t('saleInvoice.discountAmount')
              }}</label>
              <CurrencyInput
                v-model="model.input_discount"
                :currencyId="model.input_currency_id"
                @currency-change="updateCurrency"
                @input="calcualteAmount"
                class="ts-border ts-rounded ts-py-2"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <label class="ts-mb-0">{{ $t('saleInvoice.vatRate') }}(%)</label>
              <input
                v-model="model.vat_rate"
                @input="calcualteAmount"
                type="number"
                class="form-control"
              />
            </div>
            <div class="col-md-6">
              <label class="ts-mb-0">{{ $t('saleInvoice.vatAmount') }}</label>
              <CurrencyInput
                :currencyId="defaultCurrencyId"
                :value="model.vat_amount"
                :isDefault="true"
                class="ts-border ts-rounded ts-py-2 ts-bg-gray-100"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <label class="ts-mb-0">{{
                $t('saleInvoice.despositAmount')
              }}</label>
              <CurrencyInput
                :currencyId="defaultCurrencyId"
                :value="model.amount_deposit"
                :isDefault="true"
                class="ts-border ts-rounded ts-py-2 ts-bg-gray-100"
              />
            </div>
            <div class="col-md-6">
              <label class="ts-mb-0">{{ $t('saleInvoice.grandTotal') }}</label>
              <CurrencyInput
                :currencyId="defaultCurrencyId"
                :value="model.grand_total"
                :isDefault="true"
                class="ts-border ts-rounded ts-py-2 ts-bg-gray-100 ts-text-red-500 ts-font-semibold ts-font-robot"
              />
            </div>
          </div>
          <div
            class="ts-border ts-border-blue-400 ts-p-2 ts-rounded ts-space-y-2"
          >
            <div class="row">
              <div class="col-md-6">
                <label class="ts-mb-0 required">{{
                  $t('saleInvoice.amountPaid')
                }}</label>
                <CurrencyInput
                  v-model.number="model.input_amount_paid"
                  :currencyId="model.input_currency_id"
                  @currency-change="updateCurrency"
                  @input="calcualteAmount"
                  class="ts-border ts-rounded ts-py-2"
                  :class="{
                    'ts-border ts-rounded ts-border-red-500 ts-py-1': errors.has(
                      'input_amount_paid'
                    )
                  }"
                />
                <div class="validate" v-if="errors.has('input_amount_paid')">
                  {{ errors.first('input_amount_paid') }}
                </div>
              </div>
              <div class="col-md-6">
                <label class="ts-mb-0">{{ $t('saleInvoice.amountDue') }}</label>
                <CurrencyInput
                  :value="model.amount_due / model.fx_rate_ratio"
                  :currencyId="model.input_currency_id"
                  :isDefault="true"
                  class="ts-border ts-rounded ts-py-2 ts-bg-gray-100"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <label class="ts-mb-0 required">{{
                  $t('saleInvoice.paymentMethod')
                }}</label>
                <a-select
                  v-model="model.payment_method_id"
                  :options="paymentMethodList"
                  class="ts-w-full"
                  @change="onPaymentMethodChange"
                ></a-select>
                <div class="validate" v-if="errors.has('payment_method_id')">
                  {{ errors.first('payment_method_id') }}
                </div>
              </div>
              <div class="col-md-6">
                <label class="ts-mb-0">{{ $t('saleInvoice.bankName') }}</label>
                <a-select
                  v-model="model.bank_id"
                  :disabled="!allowSelectBank"
                  :options="banksList"
                  class="ts-w-full"
                  @change="onBankChange"
                ></a-select>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <label class="ts-mb-0 required">{{
                  $t('saleInvoice.accountNumber')
                }}</label>
                <a-select
                  v-model="model.account_number"
                  :disabled="!allowSelectBank"
                  :options="accountByBankId"
                  class="ts-w-full"
                  @change="setAccountName"
                ></a-select>
                <div class="validate" v-if="errors.has('account_number')">
                  {{ errors.first('account_number') }}
                </div>
              </div>
              <div class="col-md-6">
                <label class="ts-mb-0">{{
                  $t('saleInvoice.nextPaymentDate')
                }}</label>
                <a-date-picker
                  :allowClear="false"
                  :disabled="!model.amount_due > 0"
                  v-model="model.next_payment_date"
                  format="DD-MM-YYYY"
                  valueFormat="DD-MM-YYYY"
                  class="ts-w-full"
                  readonly
                />
                <div class="validate" v-if="errors.has('next_payment_date')">
                  {{ errors.first('next_payment_date') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
